import React from 'react';
import { convertirMonto, formatMonedaSimbolo } from '../../libs/moneda';
import type { CategoryPrices } from '../../libs/publicPricesSnapshot';
import type { Currency } from './CurrencyToggle';
import '../landing/brand.css';

/**
 * Precio de terneros y terneras por rango de peso (promedio mensual en remates
 * de invernada de la provincia de Buenos Aires). Responde búsquedas como
 * "precio ternero 180 kg": cada rango de peso con su precio por kilo.
 */

/** "160-180" → "160 a 180 kg"; "0-100" → "hasta 100 kg". */
export function etiquetaRango(rango: string): string {
  const [desde, hasta] = rango.split('-');
  if (!hasta) return `${rango} kg`;
  return desde === '0' ? `hasta ${hasta} kg` : `${desde} a ${hasta} kg`;
}

const inicioRango = (r: string) => Number(r.split('-')[0]) || 0;

const CATEGORIAS = [
  { clave: 'terneros', label: 'Terneros' },
  { clave: 'terneras', label: 'Terneras' },
];

const RematesRangosTable: React.FC<{
  latest: Record<string, CategoryPrices>;
  currency: Currency;
  usdRate: number | null;
  uvaToday?: number | null;
  uvaLatestMonth?: number | null;
}> = ({ latest, currency, usdRate, uvaToday = null, uvaLatestMonth = null }) => {
  // Terneros y terneras tienen rangos de peso distintos: una tabla por categoría.
  const tablas = CATEGORIAS.map(({ clave, label }) => ({
    clave,
    label,
    rangos: Object.entries(latest[clave]?.perkg ?? {})
      .filter(([, p]) => p.average > 0)
      .sort(([a], [b]) => inicioRango(a) - inicioRango(b)),
  })).filter((t) => t.rangos.length > 0);
  if (tablas.length === 0) return null;

  const precio = (v: number) =>
    formatMonedaSimbolo(convertirMonto(v, currency, usdRate, uvaToday, uvaLatestMonth), currency);

  return (
    <div
      className="lp-rangos-grid"
      style={{
        display: 'grid',
        gap: 16,
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      }}
    >
      {tablas.map((t) => (
        <div className="lp-tabla-wrap" key={t.clave}>
          <table className="lp-tabla">
            <caption style={{ textAlign: 'left', fontWeight: 600, paddingBottom: 6 }}>{t.label}</caption>
            <thead>
              <tr>
                <th scope="col">Peso</th>
                <th scope="col">Promedio</th>
              </tr>
            </thead>
            <tbody>
              {t.rangos.map(([rango, p]) => (
                <tr key={rango}>
                  <th scope="row">{etiquetaRango(rango)}</th>
                  <td>{precio(p.average)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default RematesRangosTable;
